import type { JSX } from 'react';
import type { Callback } from 'ts/base/Callback';
import { useHashState } from 'ts/base/hooks/UseHashState';
import { CounterLabel } from 'ts/commons/components/CounterLabel';
import { AddedToTaskFilter } from 'ts/commons/filters/findings/AddedToTaskFilter';
import { AssessmentFilter } from 'ts/commons/filters/findings/AssessmentFilter';
import { BlacklistRationalFilter } from 'ts/commons/filters/findings/BlacklistRationalFilter';
import { CategoryGroupTypeFilterForm } from 'ts/commons/filters/findings/CategoryGroupTypeFilter';
import type { FindingsFilterFormProps } from 'ts/commons/filters/findings/FindingsFilterForm';
import { FindingsFilterForm } from 'ts/commons/filters/findings/FindingsFilterForm';
import { FlaggingOptionFilter } from 'ts/commons/filters/findings/FlaggingOptionFilter';
import { GuidelineFilter } from 'ts/commons/filters/findings/GuidelineFilter';
import { IssueQueryFilter } from 'ts/commons/filters/findings/IssueQueryFilter';
import { PathsFilter } from 'ts/commons/filters/findings/PathsFilter';
import { RegexFilter } from 'ts/commons/filters/RegexFilter';
import type { ButtonProps } from 'ts/components/Button';
import { Button } from 'ts/components/Button';
import { Header } from 'ts/components/Header';
import { Icon } from 'ts/components/Icon';
import { MenuItem } from 'ts/components/Menu';
import type { TabPaneProps, TabProps } from 'ts/components/Tab';
import { Tab, TabPane } from 'ts/components/Tab';
import type { EBlacklistingOptionEntry } from 'typedefs/EBlacklistingOption';
import { EBlacklistingOption } from 'typedefs/EBlacklistingOption';
import type { FindingsSummaryCategoryInfo } from 'typedefs/FindingsSummaryCategoryInfo';
import type { FindingsSummaryInfo } from 'typedefs/FindingsSummaryInfo';
import styles from './FindingsSummaryFilter.module.less';

/** The available filter tabs. */
export enum EFindingsFilterTab {
	GENERAL,
	TEXT,
	CATEGORIES,
	ADVANCED
}

/** Props for the findings summary filter. */
type FindingsSummaryFilterProps = Omit<FindingsFilterFormProps, 'children'> & {
	blacklistFilter?: EBlacklistingOptionEntry;
	withTopApplyButton?: boolean;
	findingSummary: FindingsSummaryInfo;
	filteredCategoryInfos?: FindingsSummaryCategoryInfo[];
	withFlaggingOptionFilter?: boolean;
	isFetching?: boolean;
	hidePathFilter?: boolean;
} & Pick<FindingsFilterTabProps, 'advancedFilters'>;

/** The query parameter under which the index of the last selected tab is persisted. */
export const SELECTED_TAB_PARAMETER = 'selected-tab';

/** The finding filter including the surrounding form and apply button(s). */
export function FindingsSummaryFilter({
	blacklistFilter,
	findingsFilter,
	onSubmit,
	findingSummary,
	filteredCategoryInfos,
	withTopApplyButton,
	advancedFilters = false,
	withFlaggingOptionFilter = false,
	isFetching = false,
	hidePathFilter
}: FindingsSummaryFilterProps): JSX.Element {
	const [activeTab, setActiveTab] = useHashState(SELECTED_TAB_PARAMETER, EFindingsFilterTab.CATEGORIES);
	return (
		<FindingsFilterForm
			findingsFilter={findingsFilter}
			onSubmit={onSubmit}
			key={JSON.stringify(findingsFilter.toQueryParams())}
		>
			<div data-testid="findings-summary-filter">
				{withTopApplyButton ? <ApplyButton data-testid="top_apply_button_id" loading={isFetching} /> : null}
				<FindingsFilterTab
					findingSummary={findingSummary}
					filteredCategoryInfos={filteredCategoryInfos}
					blacklistFilter={blacklistFilter}
					activeIndex={activeTab}
					onTabChange={setActiveTab}
					advancedFilters={advancedFilters}
					withFlaggingOptionFilter={withFlaggingOptionFilter}
					hidePathFilter={hidePathFilter}
				/>
				<ApplyButton
					data-testid="bottom_apply_button_id"
					className={styles.bottomApplyButton}
					loading={isFetching}
				/>
			</div>
		</FindingsFilterForm>
	);
}

type FindingsFilterTabProps = {
	blacklistFilter?: EBlacklistingOptionEntry;
	activeIndex: number;
	findingSummary: FindingsSummaryInfo;
	filteredCategoryInfos?: FindingsSummaryCategoryInfo[];
	onTabChange: Callback<number>;
	withFlaggingOptionFilter: boolean;
	advancedFilters?: boolean;
	hidePathFilter?: boolean;
};

/** The main finding filter container with the tabs. The component will only work when wrapped in a FindingsFilterForm. */
export function FindingsFilterTab({
	activeIndex,
	onTabChange,
	findingSummary,
	filteredCategoryInfos,
	blacklistFilter = EBlacklistingOption.EXCLUDED.name,
	withFlaggingOptionFilter,
	advancedFilters = false,
	hidePathFilter
}: FindingsFilterTabProps) {
	const panes = getFilterPanes(
		blacklistFilter,
		findingSummary,
		advancedFilters,
		withFlaggingOptionFilter,
		filteredCategoryInfos,
		hidePathFilter
	);
	return (
		<div className={styles.tsFindingsFilterComponent} data-testid="findings-filter-component">
			<Tab
				menu={{ tabular: true, secondary: true, pointing: true, className: styles.findingFilterTabs }}
				panes={panes}
				activeIndex={activeIndex}
				renderActiveOnly={false}
				onTabChange={(event, data) => onTabChange(data.activeIndex!)}
			/>
		</div>
	);
}

function getFilterPanes(
	blacklistFilter: EBlacklistingOptionEntry,
	findingSummary: FindingsSummaryInfo,
	advancedFilters: boolean,
	withFlaggingOptionFilter: boolean,
	filteredCategoryInfos?: FindingsSummaryCategoryInfo[],
	hidePathFilter?: boolean
): TabProps['panes'] {
	return [
		{
			menuItem: <MenuItem key="general" icon={<Icon name="filter" />} content="General" />,
			pane: (
				<GeneralTab
					key="general-tab"
					blacklistFilter={blacklistFilter}
					findingSummary={findingSummary}
					advancedFilters={advancedFilters}
					withFlaggingOptionFilter={withFlaggingOptionFilter}
				/>
			)
		},
		{
			menuItem: <MenuItem key="text" icon={<Icon name="font" />} content="Text" />,
			pane: <TextTab key="text-tab" hidePathFilter={hidePathFilter} />
		},
		{
			menuItem: <MenuItem key="categories" icon={<Icon name="tags" />} content="Categories" />,
			pane: (
				<CategoriesTab
					key="categories-tab"
					findingSummary={findingSummary}
					filteredCategoryInfos={filteredCategoryInfos}
				/>
			)
		}
	];
}

type GeneralTabProps = TabPaneProps & {
	blacklistFilter: EBlacklistingOptionEntry;
	findingSummary: FindingsSummaryInfo;
	advancedFilters: boolean;
	withFlaggingOptionFilter: boolean;
};

function GeneralTab({
	blacklistFilter,
	findingSummary,
	advancedFilters,
	withFlaggingOptionFilter,
	...paneProps
}: GeneralTabProps) {
	return (
		<TabPane basic attached={false} {...paneProps}>
			<AssessmentFilter />
			<AddedToTaskFilter />
			<GuidelineFilter findingSummary={findingSummary} />
			<BlacklistRationalFilter blacklistFilter={blacklistFilter} />
			<FlaggingOptionFilter enabled={withFlaggingOptionFilter} />
			{advancedFilters ? (
				<IssueQueryFilter legendText="If provided, only findings of files which were changed in at least one of the issues matched by the provided query are shown." />
			) : null}
		</TabPane>
	);
}

type TextTabProps = TabPaneProps & {
	hidePathFilter?: boolean;
};

function TextTab({ hidePathFilter, ...paneProps }: TextTabProps) {
	return (
		<TabPane basic attached={false} {...paneProps}>
			<FindingRegexFilter />
			{hidePathFilter ? null : <PathsFilter />}
		</TabPane>
	);
}

function FindingRegexFilter() {
	return (
		<RegexFilter filterTooltip={'A regular expression to look for.\nConsidered fields:\n- Message\n- Location.'} />
	);
}

function CategoriesTab({
	findingSummary,
	filteredCategoryInfos,
	...paneProps
}: TabPaneProps & { findingSummary: FindingsSummaryInfo; filteredCategoryInfos?: FindingsSummaryCategoryInfo[] }) {
	return (
		<TabPane basic attached={false} {...paneProps}>
			<CategoryGroupTypeFilterForm
				findingSummary={findingSummary}
				filteredCategoryInfos={filteredCategoryInfos}
			/>
		</TabPane>
	);
}

/** Right aligned button that allows to submit the current filter settings. */
export function ApplyButton(props: ButtonProps): JSX.Element | null {
	return (
		<div className={styles.applyButtonContainer}>
			<Button
				primary
				basic
				type="submit"
				size="mini"
				icon={<Icon name="sync alternate" />}
				content="Apply"
				{...props}
			/>
		</div>
	);
}

/** Props for the findings count. */
type FindingsCountProps = {
	numberOfVisibleFindings: number;
	totalNumberOfFindings: number;
};

/** Shows the number of filtered vs total findings. */
export function FindingsCount({ numberOfVisibleFindings, totalNumberOfFindings }: FindingsCountProps): JSX.Element {
	return (
		<Header as="h5" style={{ marginTop: '1em' }}>
			Findings
			<CounterLabel
				count={numberOfVisibleFindings + ' / ' + totalNumberOfFindings}
				className="pull-right"
				title="Filtered findings vs. all findings"
			/>
		</Header>
	);
}
